import React, { useState } from 'react';
import {
  X,
  Check,
  Zap,
  Crown,
  Building2,
  Rocket,
  Star,
  ArrowRight,
  Clock,
  Shield,
  CreditCard,
  Sparkles
} from 'lucide-react';
import {
  SUBSCRIPTION_PLANS,
  BillingCycle,
  SubscriptionPlanDetails
} from '../types/subscription';

interface PaywallModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan?: (planId: string, billingCycle: BillingCycle) => void;
  daysRemaining?: number;
  isTrialExpired?: boolean;
}

const PaywallModal: React.FC<PaywallModalProps> = ({
  isOpen,
  onClose,
  onSelectPlan,
  daysRemaining = 0,
  isTrialExpired = true
}) => {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');

  if (!isOpen) return null;

  const getPlanIcon = (planId: string) => {
    switch (planId) {
      case 'starter':
        return <Rocket className="w-6 h-6" />;
      case 'growth':
        return <Zap className="w-6 h-6" />;
      case 'professional':
        return <Crown className="w-6 h-6" />;
      case 'enterprise':
        return <Building2 className="w-6 h-6" />;
      default:
        return <Star className="w-6 h-6" />;
    }
  };

  const getPlanGradient = (planId: string, recommended?: boolean) => {
    if (recommended) {
      return 'from-purple-500/20 via-blue-500/10 to-cyan-500/5 border-purple-500/40';
    }
    switch (planId) {
      case 'starter':
        return 'from-green-500/10 to-emerald-500/5 border-green-500/20 hover:border-green-500/40';
      case 'growth':
        return 'from-blue-500/10 to-cyan-500/5 border-blue-500/20 hover:border-blue-500/40';
      case 'professional':
        return 'from-purple-500/10 to-pink-500/5 border-purple-500/20 hover:border-purple-500/40';
      case 'enterprise':
        return 'from-amber-500/10 to-orange-500/5 border-amber-500/20 hover:border-amber-500/40';
      default:
        return 'from-gray-500/10 to-gray-500/5 border-gray-500/20';
    }
  };

  const getPlanIconColor = (planId: string) => {
    switch (planId) {
      case 'starter':
        return 'text-green-400 bg-green-500/20';
      case 'growth':
        return 'text-blue-400 bg-blue-500/20';
      case 'professional':
        return 'text-purple-400 bg-purple-500/20';
      case 'enterprise':
        return 'text-amber-400 bg-amber-500/20';
      default:
        return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getPrice = (plan: SubscriptionPlanDetails): string => {
    if (billingCycle === 'yearly') {
      const monthly = plan.yearlyPrice / 12;
      return `$${monthly.toFixed(2)}`;
    }
    return `$${plan.monthlyPrice.toFixed(2)}`;
  };

  const getTotalYearlyPrice = (plan: SubscriptionPlanDetails): string => {
    return `$${plan.yearlyPrice.toFixed(2)}`;
  };

  const handleSelectPlan = (planId: string) => {
    if (onSelectPlan) {
      onSelectPlan(planId, billingCycle);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop with blur */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-6xl max-h-[90vh] overflow-y-auto bg-[#0a0a14] rounded-3xl border border-white/10 shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="p-8 pb-4 text-center">
          {isTrialExpired ? (
            <>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 text-red-400 rounded-full text-sm font-medium mb-4">
                <Clock className="w-4 h-4" />
                Deneme Süreniz Sona Erdi
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">
                Premium'a Yükselt
              </h2>
              <p className="text-gray-400 max-w-xl mx-auto">
                E-ticaret operasyonlarınızı kesintisiz yönetmeye devam etmek için bir plan seçin.
                Tüm verileriniz güvende ve hemen erişilebilir olacak.
              </p>
            </>
          ) : (
            <>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 text-purple-400 rounded-full text-sm font-medium mb-4">
                <Sparkles className="w-4 h-4" />
                {daysRemaining} Gün Kaldı
              </div>
              <h2 className="text-3xl font-bold text-white mb-2">
                Planınızı Seçin
              </h2>
              <p className="text-gray-400 max-w-xl mx-auto">
                Deneme süreniz bitmeden planınızı seçin ve kesintisiz kullanıma devam edin.
              </p>
            </>
          )}

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-purple-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Aylık
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly'
                  ? 'bg-purple-500 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Yıllık
              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                %20 İndirim
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="p-8 pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SUBSCRIPTION_PLANS.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-2xl p-5 bg-gradient-to-br border transition-all ${getPlanGradient(plan.id, plan.recommended)} ${
                  plan.recommended ? 'ring-2 ring-purple-500 scale-[1.02]' : ''
                }`}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold ${
                    plan.recommended
                      ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
                      : 'bg-amber-500 text-black'
                  }`}>
                    {plan.badge}
                  </div>
                )}

                {/* Plan Header */}
                <div className="flex items-center gap-3 mb-4 mt-2">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getPlanIconColor(plan.id)}`}>
                    {getPlanIcon(plan.id)}
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-lg">{plan.name}</h3>
                    <p className="text-xs text-gray-400">{plan.description}</p>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-white">{getPrice(plan)}</span>
                    <span className="text-gray-400 text-sm">/ay</span>
                  </div>
                  {billingCycle === 'yearly' && (
                    <p className="text-xs text-gray-500 mt-1">
                      Yıllık {getTotalYearlyPrice(plan)} (aylık ${plan.monthlyPrice.toFixed(2)} yerine)
                    </p>
                  )}
                </div>

                {/* Limits Summary */}
                <div className="grid grid-cols-2 gap-2 mb-4 p-3 bg-black/30 rounded-xl">
                  <div className="text-center">
                    <p className="text-lg font-bold text-white">
                      {plan.limits.marketplaces === -1 ? '∞' : plan.limits.marketplaces}
                    </p>
                    <p className="text-xs text-gray-400">Pazaryeri</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-white">
                      {plan.limits.websites === -1 ? '∞' : plan.limits.websites}
                    </p>
                    <p className="text-xs text-gray-400">Web Sitesi</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-white">
                      {plan.limits.adPlatforms === -1 ? '∞' : plan.limits.adPlatforms}
                    </p>
                    <p className="text-xs text-gray-400">Reklam</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-white">
                      {plan.limits.accounting === -1 ? '∞' : plan.limits.accounting}
                    </p>
                    <p className="text-xs text-gray-400">Muhasebe</p>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2 mb-6">
                  {plan.features.slice(0, 5).map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                  {plan.features.length > 5 && (
                    <li className="text-xs text-gray-500 pl-6">
                      +{plan.features.length - 5} özellik daha
                    </li>
                  )}
                </ul>

                {/* CTA Button */}
                <button
                  onClick={() => handleSelectPlan(plan.id)}
                  className={`w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                    plan.recommended
                      ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:opacity-90'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  Planı Seç
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/5">
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-green-400" />
              <span>256-bit SSL Güvenlik</span>
            </div>
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-blue-400" />
              <span>Güvenli Ödeme</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-400" />
              <span>İstediğiniz zaman iptal</span>
            </div>
          </div>
          <p className="text-center text-xs text-gray-500 mt-4">
            Sorularınız mı var? <a href="/contact" className="text-purple-400 hover:underline">Bizimle iletişime geçin</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaywallModal;
